import { ITextInputVM } from "../AppRoutes/Journal/Feedback/Feedback";
import { ILikertVM } from "../AppRoutes/Journal/Feedback/Likert";
import { Config } from "../Services/config";
import { DataServices } from "../Services/DataService/DataService";
import { BasicEntityWatcher } from "../Services/DataService/EntityWatcher";
import { Models } from "../Services/DataService/Model";
import { FeedbackResponse } from "../Services/DataService/Model/FeedbackResponse";
import { FeedbackRules } from "../Services/DataService/Model/FeedbackRules";
import { GetToday } from "../Services/Utilities";
import { IConstructorOptions } from "../Utilities";
import { BaseViewModel, IEntityConstructorOptions } from "./Base/BaseViewModel";
import { DailyEntryViewModel } from "./DailyEntryViewModel";
import { UserProfileViewModel } from "./UserProfileViewModel";
import { GetCorrelation, ICorrelation } from "./Utilities";




export class FeedbackViewModel extends BaseViewModel {

    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //
    //  #region Static
    //
    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    public static get TypeName() { return Models.FeedbackRules; };
    public get TypedData() { return ((this.Data as unknown) as FeedbackRules); }

    public static resetInstance() {
        FeedbackViewModel.instance = undefined as any;
    }

    public static async GetModel(): Promise<FeedbackViewModel> {
        if (!FeedbackViewModel.instance) {
            let rulesData = await DataServices.ReadOnly.readData(FeedbackRules.CreateId());
            FeedbackViewModel.instance = new FeedbackViewModel({ sourceObject: rulesData });
        }
        return FeedbackViewModel.instance;
    }
    private static instance: FeedbackViewModel;

    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //
    //  #endregion Static
    //
    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////





    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //
    //  #region Publics
    //
    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    public get ShowFeedbackButton(): boolean {
        return this._showFeedbackButton;
    }


    public get FeedbackTasks() {
        //
        // Need to randomize the order of the tasks before we return them
        //
        let tasks = this.TypedData.feedbackTasks;
        let tasksCopy = [...tasks];
        let currentIndex = tasksCopy.length;
        let randomIndex: number;
        let temporaryValue: string;
        while (currentIndex !== 0) {
            randomIndex = Math.floor(Math.random() * currentIndex);
            currentIndex--;
            temporaryValue = tasksCopy[currentIndex];
            tasksCopy[currentIndex] = tasksCopy[randomIndex];
            tasksCopy[randomIndex] = temporaryValue;
        }

        return tasksCopy;
    }




    public get Page1VMs() {
        return {
            csat: this._csatVM!,
            nps: this._npsVM!
        };
    }

    public get Page2VMs() {
        return {
            constructiveFeedback: this._constructiveFeedback!,
            positiveFeedback: this._positiveFeedback!
        };
    }

    public get Page3VMs() {
        return {
            easeOfUse: this._easeOfUseVM!,
            technicalReliability: this._technicalReliabilityVM!,
            featuresCapabilities: this._featuresCapabilitiesVM!,
            visualAppeal: this._visualAppealVM!,
            speed: this._speedVM!
        };
    }

    public get Page4VMs() {

        //
        // Remove the taskVMs that are now unselected
        //
        this._taskVMs = this._taskVMs.filter((task) => {
            var index = this.TypedData.feedbackTasks.findIndex((t) => t === task.title);
            return this._selectedTaskIndexes.includes(index)
        });

        //
        // Get the list of taskVMs that need to be added
        //
        let taskTitles = this._taskVMs.map((t) => t.title);
        let newTasksIndex = this._selectedTaskIndexes.filter(
            (t) => !taskTitles.includes(this.TypedData.feedbackTasks[t])
        );

        //
        // Add the missing taskVMs
        //
        for (let taskIndex of newTasksIndex) {

            const task = this.TypedData.feedbackTasks[taskIndex];

            if (this._feedbackResponse.tasksFeedback.find((t) => t.task === task))
                continue;

            this._feedbackResponse.tasksFeedback.push({ task: task, score: undefined });
            let index = this._feedbackResponse.tasksFeedback.length - 1;
            let previousIndex = index - 1;

            this._taskVMs.push(
                {
                    title: task,
                    onScoreChange: (value) => {
                        this._feedbackResponse.tasksFeedback[index].score = value;
                        this.FireChanged("Page4VMs");
                    },
                    scoreFN: () => this._feedbackResponse.tasksFeedback[index].score,
                    shouldShow: () => previousIndex < 0 || this._feedbackResponse.tasksFeedback[previousIndex].score !== undefined,
                }
            );
        }

        return {
            isChecked: (index: number) => this._selectedTaskIndexes.includes(index),
            onSelectionChanged: (index: number) => {
                if (this._selectedTaskIndexes.includes(index))
                    this._selectedTaskIndexes = this._selectedTaskIndexes.filter((t) => t !== index);
                else
                    this._selectedTaskIndexes = [...this._selectedTaskIndexes, index];
                this.FireChanged("Page4VMs");
            },
            allTasks: this.TypedData.feedbackTasks,
            taskVMs: this._taskVMs,
            showNextButton: () => this._selectedTaskIndexes.length > 0,
            showFinishButton: () => this._taskVMs.length > 0 && this._taskVMs[this._taskVMs.length - 1].scoreFN() !== undefined
        };
    }

    public async FeedbackStarted() {
        this._userProfile!.TypedData.lastFeedbackDate = GetToday();
        await this.evaluateRules();
        this.FireChanged("ShowFeedbackButton");
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //
    //  #endregion Publics
    //
    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////





    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //
    //  #region Protected/Privates
    //
    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    protected async doHydrate(_correlation: ICorrelation): Promise<void> {
        this._userProfile = await UserProfileViewModel.GetModel();
        await this.evaluateRules();
    }

    protected CreateData(_watcher: BasicEntityWatcher, options?: IConstructorOptions) {
        return FeedbackRules.Create(options);
    }


    private async evaluateRules() {

        if (this.feedbackDisabledRule()) {
            this._showFeedbackButton = false;
            return;
        }

        var dailyEntries = await DailyEntryViewModel.Manager.GetAllItems(GetCorrelation());

        var newUser = this.newUserRule(dailyEntries);
        var newRelease = this.newReleaseRule(dailyEntries);
        var waitFeedback = this.waitFeedbackRule();

        var result = (newUser || newRelease) && waitFeedback;
        this._showFeedbackButton = result;
    }






    /**
     * 
     * newUserRule
     * 
     *    This rule checks if the user is new and has either been using the app for a
     *   certain number of days or has completed a certain number of daily entries.
     * 
     */
    private newUserRule(dailyEntries: DailyEntryViewModel[]) {

        let createdDate = this._userProfile!.TypedData.createdDate;

        //
        // First calculate the number of days since the user was created
        //
        let timespan = GetToday().getTime() - createdDate.getTime();
        let numDays = Math.floor(timespan / (1000 * 3600 * 24));

        //
        // Next calculate the number of daily entries the user has completed
        //
        let numEntries = dailyEntries.length;

        //
        // Now check the rules
        //
        let ruleNumDays = this.TypedData.newUserDays;
        let ruleNumEntries = this.TypedData.newUserEntries;
        let ruleNumDaysEnd = this.TypedData.newUserDaysEnd;

        //
        // First lets sanity check our inputs
        //
        if (ruleNumDays === undefined || ruleNumEntries === undefined || ruleNumDaysEnd === undefined)
            return false;

        if ((numDays >= ruleNumDays || numEntries >= ruleNumEntries) && numEntries > 0 && numDays <= ruleNumDaysEnd) {
            return true;
        }
        return false;
    }






    private newReleaseRule(dailyEntries: DailyEntryViewModel[]) {

        //
        // get the rules
        //
        let ruleNumDays = this.TypedData.newReleaseDays;
        let ruleNumEntries = this.TypedData.newReleaseEntries;
        let ruleNumDaysEnd = this.TypedData.newReleaseDaysEnd;

        //
        // First check to see if the version is different
        //
        let lastVersion = this._userProfile!.TypedData.appVersion;
        if (lastVersion !== Config.AppVersion || lastVersion == undefined) {
            this._userProfile!.TypedData.appVersion = Config.AppVersion;
            this._userProfile!.TypedData.appVersionDate = GetToday();
        }

        //
        // Next calculate the number of days since the appVersion changed
        //
        let lastReleaseDate = this._userProfile!.TypedData.appVersionDate;
        let timespan = lastReleaseDate ? GetToday().getTime() - lastReleaseDate.getTime() : ruleNumDays;
        let numDays = Math.floor(timespan / (1000 * 3600 * 24));

        //
        // Next calculate the number of daily entries the user has completed
        //
        let entries = dailyEntries.filter((entry) => entry.TypedData.date >= lastReleaseDate!);
        let numEntries = entries.length;

        //
        // Now check the rules
        //
        var retVal = false;
        if ((numDays >= ruleNumDays || numEntries >= ruleNumEntries) && numDays <= ruleNumDaysEnd) {
            retVal = true;
        }
        return retVal;
    }






    private waitFeedbackRule() {

        //
        // get the rules
        //
        let ruleNumDays: number;
        switch (this._userProfile?.TypedData.appRing) {
            case "Production":
                ruleNumDays = this.TypedData.waitDaysStandard;
                break;
            case "PublicPreview":
                ruleNumDays = this.TypedData.waitDaysPublicPreview;
                break;
            case "Selfhost":
                ruleNumDays = this.TypedData.waitDaysSelfhost;
                break;
            default:
                throw new Error("Unknown appRing");
        }
        ruleNumDays = 2; // For testing

        //
        // Next calculate the number of days since the last feedback
        //
        let lastFeedbackDate = this._userProfile!.TypedData.lastFeedbackDate;
        let numDays: number;
        if (lastFeedbackDate) {
            let timespan = GetToday().getTime() - lastFeedbackDate.getTime();
            numDays = Math.floor(timespan / (1000 * 3600 * 24));
        } else {
            numDays = ruleNumDays;
        }

        //
        // Now check the rules
        //
        if (numDays >= ruleNumDays) {
            return true;
        }
        return false;
    }


    private feedbackDisabledRule() {

        if (this._userProfile === undefined || this._userProfile?.TypedData.isTestAccount)
            return true;

        return this.TypedData.feedbackDisabled;
    }



    private SetLikertVMs() {

        //
        // Page 1 VMs
        //

        this._csatVM = {
            title: "Overall, how satisfied are you with Growth Lenses?",
            onScoreChange: (value) => {
                this._feedbackResponse.csatResponse = value;
                this.FireChanged("Page1VMs");
            },
            scoreFN: () => this._feedbackResponse.csatResponse,
            shouldShow: () => true
        };

        this._npsVM = {
            title: "How likely are you to recommend Growth Lenses to a friend or colleague?",
            onScoreChange: (value) => {
                this._feedbackResponse.npsResponse = value;
                this.FireChanged("Page1VMs");
            },
            scoreFN: () => this._feedbackResponse.npsResponse,
            shouldShow: () => this._feedbackResponse.csatResponse !== undefined
        };


        //
        // Page 2 VMs
        //

        this._constructiveFeedback = {
            title: "What, if anything, do you find frustrating or unappealing about the GrowthLenses Journal? What new capabilities would you like to see for the GrowthLenses Journal?",
            valueFN: () => this._feedbackResponse.constructiveFeedback ?? '',
            onChange: (e) => {
                this._feedbackResponse.constructiveFeedback = e.target.value;
                this.FireChanged("Page2VMs");
            }
        };

        this._positiveFeedback = {
            title: "What do you like about the GrowthLenses Journal?",
            valueFN: () => this._feedbackResponse.positiveFeedback ?? '',
            onChange: (e) => {
                this._feedbackResponse.positiveFeedback = e.target.value;
                this.FireChanged("Page2VMs");
            }
        }

        //
        // Page 3 VMs
        //

        this._easeOfUseVM = {
            title: "Ease of use",
            onScoreChange: (value) => {
                this._feedbackResponse.easeOfUse = value;
                this.FireChanged("Page3VMs");
            },
            scoreFN: () => this._feedbackResponse.easeOfUse,
        };

        this._technicalReliabilityVM = {
            title: "Technical reliability",
            onScoreChange: (value) => {
                this._feedbackResponse.technicalReliability = value;
                this.FireChanged("Page3VMs");
            },
            scoreFN: () => this._feedbackResponse.technicalReliability,
            shouldShow: () => this._feedbackResponse.easeOfUse !== undefined,
        };

        this._featuresCapabilitiesVM = {
            title: "Features and capabilities",
            onScoreChange: (value) => {
                this._feedbackResponse.featuresCapabilities = value;
                this.FireChanged("Page3VMs");
            },
            scoreFN: () => this._feedbackResponse.featuresCapabilities,
            shouldShow: () => this._feedbackResponse.technicalReliability !== undefined,
        };

        this._visualAppealVM = {
            title: "Visual appeal",
            onScoreChange: (value) => {
                this._feedbackResponse.visualAppeal = value;
                this.FireChanged("Page3VMs");
            },
            scoreFN: () => this._feedbackResponse.visualAppeal,
            shouldShow: () => this._feedbackResponse.featuresCapabilities !== undefined,
        };

        this._speedVM = {
            title: "Speed",
            onScoreChange: (value) => {
                this._feedbackResponse.speed = value;
                this.FireChanged("Page3VMs");
            },
            scoreFN: () => this._feedbackResponse.speed,
            shouldShow: () => this._feedbackResponse.visualAppeal !== undefined,
        };
    }

    private constructor(options?: IEntityConstructorOptions) {
        super(options);


        this._feedbackResponse = FeedbackResponse.Create(DataServices.EntityWatcher);

        this.SetLikertVMs();
    }


    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //
    //  #endregion Protected/Privates
    //
    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    private _feedbackResponse: FeedbackResponse;

    private _csatVM: ILikertVM | undefined;
    private _npsVM: ILikertVM | undefined;

    private _constructiveFeedback: ITextInputVM | undefined;
    private _positiveFeedback: ITextInputVM | undefined;

    private _easeOfUseVM: ILikertVM | undefined;
    private _technicalReliabilityVM: ILikertVM | undefined;
    private _featuresCapabilitiesVM: ILikertVM | undefined;
    private _visualAppealVM: ILikertVM | undefined;
    private _speedVM: ILikertVM | undefined

    private _taskVMs: ILikertVM[] = [];
    private _selectedTaskIndexes: number[] = [];


    private _userProfile: UserProfileViewModel | undefined = undefined;
    private _showFeedbackButton: boolean = false;
}